// =============================================================================
// ui/tabs/PropReport.tsx — the portfolio view across every prop account.
// -----------------------------------------------------------------------------
// Transcribed from Josh's two screenshots. HEALTH is ported here; ECONOMICS is
// named on screen and not built — see the note at the foot of the file.
//
// ── 🚨 THIS TAB BLOCKS RATHER THAN CAVEATS (D47) ──
//
// Combined DD remaining, every progress bar, every profit-target percentage: all
// functions of the ENTIRE trade history. Under a bounded read they do not read as
// uncertain, they read as MORE ROOM THAN EXISTS — and this is the screen a trader
// checks before sizing up.
//
// So the tab renders nothing until `usePropUniverse` has proven a whole book. It
// does not render "approximately", it does not render a spinner over stale
// numbers, and it does not fall back to the range-scoped read that every other
// tab uses. Either the population is whole or the cards are not drawn.
//
// ── ⚠ IT IGNORES THE GLOBAL DATE FILTER, AND THAT IS NOT AN OVERSIGHT ──
//
// Beta anchors this tab to year-to-date "without depending on the trade-period
// chip (which is trade-centric, not account-centric)". A compliance view that
// answers "am I safe?" differently because a filter above it says "This week" is
// not a compliance view. The YTD window below is computed from the range
// ANCHOR — today's date — never from the selection.
// =============================================================================

import { useMemo, useState } from 'react';
import { useAccounts } from '../state/accounts';
import { usePreferences } from '../state/preferences';
import { useRange } from '../state/range';
import { usePropUniverse } from '../state/usePropUniverse';
import {
  dailyPnlHeatmap, fundedStatus, groupPropAccounts, liveAccountIds, progressBars,
  propTiltSignals,
  type Heatmap, type ProgressBar, type PropAccount, type TiltComposite,
} from '../../selectors/propReport';
import { EduMark } from '../components/EduMark';
import type { EconomicsAccount } from '../../selectors/economics';
import { EconomicsView } from './PropEconomics';
import { money, moneyTarget } from '../format';

type SubTab = 'health' | 'economics';

/** Beta's tone → this client's tokens. `--green` is BLUE here; `#22c55e` is not. */
const TONE: Record<ProgressBar['tone'], string> = {
  green: '#22c55e',
  blue: 'var(--blue)',
  amber: '#fb923c',
  orange: '#f97316',
};

export function PropReportTab() {
  const { accounts } = useAccounts();
  const { prefs } = usePreferences();
  const { today } = useRange();
  const { phase, population, error } = usePropUniverse();
  const mode = prefs?.dayBoundaryMode ?? null;

  const [sub, setSub] = useState<SubTab>('health');

  const groups = useMemo(
    () => groupPropAccounts((accounts ?? []) as unknown as readonly PropAccount[]),
    [accounts],
  );

  const health = useMemo(() => {
    if (!population || !today || !mode) return null;
    // ⚠ YTD from the range ANCHOR, never from the selection — see the header.
    const ytdStart = `${today.slice(0, 4)}-01-01`;
    return {
      status: fundedStatus(groups, population, ytdStart, today, today, mode),
      // ⚠ Signed for the RESULT, unsigned for the GOAL — see `MoneyFormats`.
      bars: progressBars(groups, population, today, mode, {
        signed: (n) => money(n, 0),
        plain: (n) => moneyTarget(n),
      }),
      heat: dailyPnlHeatmap(groups, population, today),
      // ⚠ Funded + eval only — the accounts he can still blow. A tilt warning
      // about an account that is already closed is noise on a compliance screen.
      tilt: propTiltSignals(liveAccountIds(groups), population),
    };
  }, [groups, population, today, mode]);

  // ── the blocked and failed states ──────────────────────────────────────────

  if (phase === 'failed') {
    return (
      <div className="card" role="alert">
        <h3>Couldn’t load your whole trade history</h3>
        <p className="muted">
          Nothing has been changed — this is a read problem. This tab needs every trade
          you have, because drawdown room, profit-target progress and consistency are
          all measured against your entire history. Computed from part of it they would
          read as <b>more room than you actually have</b>, so they are not being drawn.
        </p>
        <p className="muted">{error}</p>
      </div>
    );
  }

  if (!accounts) return <p className="muted">Loading your accounts…</p>;

  const totalAccounts = Object.values(groups).reduce((s, g) => s + g.length, 0);
  if (!totalAccounts) {
    return (
      <div className="prop-tab">
        <PropReportHeader />
        <div className="card">
          <h3>No accounts configured yet</h3>
          <p className="muted">
            Add an account in <b>Accounts</b> to populate this view.
          </p>
        </div>
      </div>
    );
  }

  if (!health) return <p className="muted">Loading your whole trade history…</p>;

  return (
    <div className="prop-tab">
      <PropReportHeader />

      <div className="pr-tabs" role="tablist">
        <button
          type="button" role="tab" aria-selected={sub === 'health'}
          className={sub === 'health' ? 'on' : ''} onClick={() => setSub('health')}
        >Health</button>
        <button
          type="button" role="tab" aria-selected={sub === 'economics'}
          className={sub === 'economics' ? 'on' : ''} onClick={() => setSub('economics')}
        >Economics</button>
      </div>

      {sub === 'health' ? (
        <>
          <TiltBanner tilt={health.tilt} />

          <section className="card pr-card">
            <h3>Funded Status</h3>
            <div className="pr-cells">
              <Cell
                label="Portfolio composition"
                value={<><b>{health.status.fundedCount}</b> <span className="pr-unit">funded</span></>}
                sub={composition(health.status)}
              />
              <Cell
                label="Combined buying power"
                value={moneyTarget(health.status.combinedBuyingPower)}
                sub="Total funded account size"
              />
              <Cell
                label="Combined DD remaining"
                // ⚠ Beta turns this orange below $1,000 — a fixed threshold, not a
                // ratio. On a portfolio of one small account that is nearly always
                // orange; matched anyway, because it is the number the colour is
                // there to make you look at.
                value={moneyTarget(health.status.combinedDDRemaining)}
                cls={health.status.combinedDDRemaining < 1000 ? 'warn' : ''}
                sub="Aggregate cushion across funded"
              />
              <Cell
                label="YTD net (operation)"
                // ⚠ ONE sign, not two. Beta renders `+` and then calls a formatter
                // that also prepends one, so this cell reads "++$195" on screen.
                // See the note at the foot of this file.
                value={money(health.status.ytdNetOperation, 0)}
                cls={health.status.ytdNetOperation >= 0 ? 'green' : 'red'}
                sub="Trade P&L + Payouts − Fees"
              />
            </div>
          </section>

          {health.bars.length ? (
            <section className="card pr-card">
              <h3>Account Progress</h3>
              <p className="muted">
                Each bar: DD floor on the left, goal on the right. Eval accounts show
                progress to profit target; funded accounts show DD cushion remaining.
              </p>
              {health.bars.map((b) => <Bar key={b.accountId} bar={b} />)}
            </section>
          ) : null}

          {health.heat && health.heat.maxAbs > 0 ? <HeatCard heat={health.heat} /> : null}

          <NotBuiltHereCard />
        </>
      ) : (
        <EconomicsView
          accounts={(accounts ?? []) as unknown as readonly EconomicsAccount[]}
          population={population}
          today={today}
        />
      )}
    </div>
  );
}

/**
 * The Tilt Signal banner — the first thing on this screen when it has something
 * to say, and nothing at all when it does not.
 *
 * ⚠ NOTHING RENDERS BELOW TWO SIGNALS, INCLUDING NO "you look fine" CARD. Beta
 * does the same and it is the right call twice over: a banner that is always
 * present stops being read, and "no tilt detected" is reassurance this data
 * cannot support — the tagged-mistake signal only fires on trades the trader
 * remembered to flag, and Josh has flagged none of 820.
 */
export function TiltBanner({ tilt }: { tilt: TiltComposite }) {
  if (!tilt.available || tilt.level === 'calm') return null;
  const risk = tilt.level === 'risk';

  return (
    <section className={`pr-tilt ${risk ? 'risk' : 'elevated'}`} role="status">
      <div className="pr-tilt-head">
        <span className="pr-tilt-dot" aria-hidden="true" />
        <span className="pr-tilt-verdict">
          {risk ? 'Tilt risk' : 'Elevated'} — {tilt.score} signal{tilt.score === 1 ? '' : 's'} firing
        </span>
        <span className="muted pr-tilt-src">
          Tilt Signal<EduMark eduKey="analytics.tilt-signal" /> · observed + tagged
        </span>
      </div>

      <p className="pr-tilt-advice">{tilt.advice}</p>

      <ul className="pr-tilt-list">
        {tilt.reasons.map((r) => (
          <li key={r.key}><b>{r.label}:</b> <span className="muted">{r.detail}</span></li>
        ))}
      </ul>

      {/* 🚨 PD-29 — THE LINE BETA DOES NOT HAVE, AND THE ONE THAT STOPS THIS
          BANNER LYING. Every window above is measured back from the trader's
          LAST TRADE, not from now: that is deliberate (it makes the signal work
          on imported history), but it means a banner built from a session five
          days ago renders identically to one built from this morning. On a
          screen a trader checks BEFORE sizing up, "19 consecutive losers" with
          no date on it is a live warning about something that may be over. */}
      {tilt.asOf ? (
        <p className="muted pr-tilt-asof">
          Measured from your last trade — {tilt.asOf.date}
          {tilt.asOf.time ? ` at ${tilt.asOf.time.slice(0, 5)}` : ''}. If that was a
          while ago, this describes that session, not right now.
        </p>
      ) : null}
    </section>
  );
}

/**
 * What this screen does not do, said out loud.
 *
 * ⚠ NAMED, not silently absent. Risk of Ruin is gated in beta too — it needs
 * 20+ trades on an account — but "missing because gated" and "missing because
 * unported" look identical on an empty screen.
 *
 * 🔴 AND THE SECOND PARAGRAPH IS THE ONE THAT WAS OVERDUE. Four of the five
 * cards on the Economics tab — Lifetime Cash, Eval Pipeline, Return on
 * Investment and Payout Velocity — CANNOT HOLD DATA, because nothing anywhere in
 * this client can record a payout or a fee. They are not empty because the
 * trader has done nothing; they are empty because no write path exists.
 *
 * Until TCR-17 that was declared only in a code comment above the missing
 * button, where no trader could read it. Four cards that can never fill, with no
 * explanation, read as broken software — and every other pane in this client
 * already follows the rule that an absence gets named on screen.
 */
export function NotBuiltHereCard() {
  return (
    <section className="card">
      <h3>Not on this screen yet</h3>
      <p className="muted">
        Beta’s Health tab also carries <b>Risk of Ruin</b> gauges — a Monte Carlo
        resample of your win rate and R-multiples, gated to accounts with 20+
        trades. Not built here. The numbers above are unaffected: they read from
        your whole trade history.
      </p>
      <p className="muted">
        There is also no way yet to <b>log a payout or a fee</b> — a payout, eval fee,
        reset fee, activation fee, withdrawal, deposit or correction against an
        account. Beta logs these from a button on this screen. Until it is built,
        Lifetime Cash Position, Eval Pipeline, Return on Investment and Payout
        Velocity on the Economics tab stay empty however much you have traded:
        they are fed by those entries, not by your trades.
      </p>
    </section>
  );
}

/**
 * ⚠ EXPORTED FOR ITS TEST, for the same reason `TiltBanner` is: mounting
 * `PropReportTab` needs four providers, and a test that has to build a whole
 * shell to assert one `?` mark is a test nobody maintains.
 */
export function PropReportHeader() {
  return (
    <div className="pr-head">
      <div>
        <h2>Prop Report<EduMark eduKey="analytics.prop-report" /></h2>
        <p className="muted">
          Portfolio view across every prop account. Centralizes the metrics that don’t
          belong on any single account’s detail page.
        </p>
      </div>
      {/* ⚠ Beta has "+ Log transaction" here, opening a quick-log modal that writes
          an adjustment. Not built — it is a WRITE path, and shipping the button
          before the write is worse than not shipping the button.

          ▶ TCR-75 carries it. And because the omission used to be declared ONLY
          in this comment, where no trader can read it, `NotBuiltHereCard` now
          says so on screen — which is the rule every other pane here follows. */}
    </div>
  );
}

function composition(s: {
  evalCount: number; passedCount: number; pausedCount: number;
  blownCount: number; closedCount: number;
}): string {
  // ⚠ Beta's exact assembly: evals always shown, the rest only when non-zero.
  // A composition line reading "0 passed · 0 paused · 0 blown" on a clean
  // portfolio is noise; their absence is the signal.
  const parts = [`${s.evalCount} eval${s.evalCount === 1 ? '' : 's'} active`];
  if (s.passedCount) parts.push(`${s.passedCount} passed`);
  if (s.pausedCount) parts.push(`${s.pausedCount} paused`);
  if (s.blownCount) parts.push(`${s.blownCount} blown`);
  if (s.closedCount) parts.push(`${s.closedCount} closed`);
  return parts.join(' · ');
}

function Cell(
  { label, value, sub, cls }:
  { label: string; value: React.ReactNode; sub: string; cls?: string },
) {
  return (
    <div className="pr-cell">
      <div className="pr-cell-k">{label}</div>
      <div className={`pr-cell-v ${cls ?? ''}`}>{value}</div>
      <div className="muted pr-cell-s">{sub}</div>
    </div>
  );
}

function Bar({ bar }: { bar: ProgressBar }) {
  const pct = Math.max(0, Math.min(100, bar.pct));
  return (
    <div className="pr-bar-row">
      <div className="pr-bar-head">
        <span className="pr-bar-name">{bar.name}</span>
        <span className="muted pr-bar-firm">{bar.firm}</span>
      </div>
      <div
        className="pr-bar" role="progressbar"
        aria-valuenow={Math.round(pct)} aria-valuemin={0} aria-valuemax={100}
        aria-label={`${bar.name}: ${bar.label}`}
      >
        <div className="pr-bar-fill" style={{ width: `${pct}%`, background: TONE[bar.tone] }} />
        {/* The position marker — beta draws it at the same percentage as the fill
            edge, so it reads as a tick on the end of the bar rather than a
            separate value. */}
        <div className="pr-bar-mark" style={{ left: `${pct}%` }} />
      </div>
      <div className="muted pr-bar-label">{bar.label}</div>
    </div>
  );
}

function HeatCard({ heat }: { heat: Heatmap }) {
  const colorFor = (v: number | null) => {
    if (v === null || v === 0) return 'var(--bg-3)';
    const ratio = Math.min(1, Math.abs(v) / heat.maxAbs);
    const alpha = (0.18 + ratio * 0.72).toFixed(2);
    // ⚠ Beta's literal rgb values, not the tokens: the ramp needs a variable
    // alpha and `var(--green)` cannot carry one. They happen to BE the tokens'
    // values — `--green` is #2e9dff — which is why the copy says "blue".
    return v > 0 ? `rgba(46,157,255,${alpha})` : `rgba(246,104,94,${alpha})`;
  };

  return (
    <section className="card pr-card">
      <h3>Daily P&amp;L by Account · Last 30 Days</h3>
      {/* ⚠ "RED", NOT "ORANGE". Beta's legend calls the losing colour orange in
          its first sentence and red in its second, and the cells render RED —
          `rgba(246,104,94)`, which is `--red`. One sentence, two names, neither
          reliably matching the squares underneath it. The word here is the one
          the cells actually are. */}
      <p className="muted">
        Each row is one account; each cell is one day’s P&amp;L. <b className="blue">Blue</b> =
        gain, <b className="red">red</b> = loss. Same-day red across multiple rows =
        your accounts are correlated (one strategy with leverage, not three strategies).
      </p>
      <div className="pr-heat-wrap">
        <div className="pr-heat">
          {heat.rows.map((r) => (
            <div className="pr-heat-row" key={r.accountId}>
              <div className="pr-heat-name">{r.name}</div>
              <div className="pr-heat-cells">
                {r.cells.map((v, i) => (
                  <div
                    key={heat.dateCols[i]}
                    className="pr-heat-cell"
                    style={{ background: colorFor(v) }}
                    // ⚠ "no trades" and a scratch are different days, and the
                    // tooltip is the only place that says which.
                    title={v == null
                      ? `${heat.dateCols[i]} · no trades`
                      : `${heat.dateCols[i]} · ${money(v, 0)}`}
                  />
                ))}
              </div>
            </div>
          ))}
          <div className="pr-heat-row">
            <div className="pr-heat-name" />
            <div className="pr-heat-cells pr-heat-axis">
              {heat.dateCols.map((d, i) => (
                <div key={d}>{i % 7 === 0 ? d.slice(5) : ''}</div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
